import {useEffect,useRef,useState,type DragEvent} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Button,cn} from '@aiw/ui';
import {RECORD_FILE_ACCEPT,RECORD_FILE_CATEGORIES,RECORD_FILE_MAX_BYTES,type RecordFileCategory} from '@artemis-group/aiw-contracts';
import {api,client,authHeaders,fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';

/**
 * Files on every Opportunity and Job (18 October 2026). One panel for both pages: drop or choose files, pick a
 * category, add a note; each file is checked in the browser (sha256), reserved with `reserve_record_file`, sent to
 * the API in 8 MiB chunks (three tries each) and confirmed only after the API has read the stored file back and its
 * checksum matched. The same file twice answers the one already kept. A Job also lists the files of the Opportunity
 * it came from, and the Opportunity's traced plans are listed read-only with a Plan tag (they stay in their own store).
 * Hide takes a file off the list; nothing is ever deleted, and "Show hidden" brings it back for Restore.
 */
export interface RecordFile {id:string;kind:'file'|'plan';readonly:boolean;category:string;name:string;note:string|null;content_type:string;size:number;status:'reserved'|'verified';hidden:boolean;source:string;by:'emma'|'you'|'staff';created_at:string;filed_on:'opportunity'|'job'}
type Owner={opportunityId:string;opportunityRef:string;jobRef?:undefined}|{jobRef:string;opportunityId?:undefined;opportunityRef?:undefined};
type Progress={key:string;name:string;text:string;tone:'busy'|'ok'|'error'};

const base=()=>`${env.apiBase}/api/record-files`;
async function readFiles(query:string):Promise<RecordFile[]>{
  if(env.mock)return [];
  const r=await fetchImpl(`${base()}?${query}`,{headers:await authHeaders()});
  const body=await r.json().catch(()=>({})) as {data?:RecordFile[];error?:string};
  if(!r.ok||!body.data)throw Error(body.error??`api ${r.status}`);
  return body.data;
}
async function hex(file:File):Promise<string>{
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',await file.arrayBuffer())),b=>b.toString(16).padStart(2,'0')).join('');
}
const pause=(ms:number)=>new Promise(r=>setTimeout(r,ms));
/** Three tries for a network failure or a 5xx; a 4xx is an answer and is returned at once. */
async function tries(run:()=>Promise<Response>):Promise<Response>{
  let last:unknown=null;
  for(let i=0;i<3;i++){
    try{const r=await run();if(r.status<500)return r;last=r;}catch(e){last=e;}
    if(i<2)await pause(1000*(i+1));
  }
  if(last instanceof Response)return last;
  throw last instanceof Error?last:Error('network');
}
const errorOf=async(r:Response)=>((await r.json().catch(()=>null)) as {error?:string}|null)?.error??`api ${r.status}`;
/** Keep one file on a Job without the panel (the crew invoice form, 25 Oct 2026): the same reserve → chunks → verify
 * path the panel takes, answering the kept file's id. The same bytes twice answer the file already kept. */
export async function keepJobFile(jobRef:string,file:File,category:RecordFileCategory,note:string):Promise<string>{
  if(file.size===0||file.size>RECORD_FILE_MAX_BYTES)throw Error('File is empty or over 100 MB');
  const sha256=await hex(file);
  const reserved=await api.propose(client,{command:'reserve_record_file',args:{job_ref:jobRef,category,name:file.name,content_type:file.type,size:file.size,sha256,...(note.trim()?{note:note.trim()}:{}),source:'upload'},idempotency_key:`record-file:${jobRef}:${sha256}:${Date.now()}`});
  if(reserved.status!=='executed')throw Error(reserved.status==='pending'?'pending approval':reserved.reason);
  const held=reserved.result as {id:string;status:string;chunk_size:number;chunks:number};
  if(held.status==='verified')return held.id;
  const headers=await authHeaders();
  for(let n=0;n<held.chunks;n++){
    const r=await tries(()=>fetchImpl(`${base()}/${held.id}/chunks/${n}`,{method:'PUT',headers:{...headers,'content-type':'application/octet-stream'},body:file.slice(n*held.chunk_size,Math.min(file.size,(n+1)*held.chunk_size))}));
    if(r.status===409)break;
    if(!r.ok)throw Error(await errorOf(r));
  }
  const done=await tries(()=>fetchImpl(`${base()}/${held.id}/complete`,{method:'POST',headers}));
  if(!done.ok)throw Error(await errorOf(done));
  return held.id;
}
const sydney=(iso:string)=>new Date(iso).toLocaleString('en-AU',{timeZone:'Australia/Sydney',day:'numeric',month:'short',year:'numeric',hour:'numeric',minute:'2-digit'});
const mb=(n:number)=>(n/1048576).toFixed(n<1048576?2:1);

/** `className` replaces the default top rule, for a page that gives the panel a card of its own. */
export function RecordFiles(props:Owner&{className?:string}){
  const {t}=useT();
  const query=props.jobRef?`job=${encodeURIComponent(props.jobRef)}`:`opportunity=${encodeURIComponent(props.opportunityRef!)}`;
  const [showHidden,setShowHidden]=useState(false);
  const files=useQuery({queryKey:['record-files',query,showHidden],queryFn:()=>readFiles(query+(showHidden?'&include_hidden=1':''))});
  const [category,setCategory]=useState<RecordFileCategory>('plans');
  const [note,setNote]=useState('');
  const [progress,setProgress]=useState<Progress[]>([]);
  const [busy,setBusy]=useState(false),[over,setOver]=useState(false);
  const [notice,setNotice]=useState('');
  const [opened,setOpened]=useState<{name:string;url:string}|null>(null);
  const [editing,setEditing]=useState<{id:string;note:string}|null>(null);
  const input=useRef<HTMLInputElement>(null);
  useEffect(()=>()=>{if(opened)URL.revokeObjectURL(opened.url);},[opened]);

  const catLabel=(c:string)=>{switch(c){case 'plans':return t('files.cat.plans');case 'photos':return t('files.cat.photos');case 'supplier':return t('files.cat.supplier');case 'customer':return t('files.cat.customer');default:return t('files.cat.other');}};
  const who=(b:RecordFile['by'])=>b==='emma'?t('rail.emma'):b==='you'?t('files.by.you'):t('files.by.staff');
  const set=(key:string,name:string,text:string,tone:Progress['tone']='busy')=>setProgress(p=>{const next=p.filter(x=>x.key!==key);return [...next,{key,name,text,tone}];});

  async function send(file:File,key:string){
    if(file.size===0||file.size>RECORD_FILE_MAX_BYTES){set(key,file.name,t('files.tooBig',{name:file.name}),'error');return;}
    try{
      set(key,file.name,t('files.hashing',{name:file.name}));
      const sha256=await hex(file);
      set(key,file.name,t('files.reserving',{name:file.name}));
      const owner=props.jobRef?{job_ref:props.jobRef}:{opportunity_id:props.opportunityId};
      const reserved=await api.propose(client,{command:'reserve_record_file',args:{...owner,category,name:file.name,content_type:file.type,size:file.size,sha256,...(note.trim()?{note:note.trim()}:{}),source:'upload'},idempotency_key:`record-file:${props.jobRef??props.opportunityId}:${sha256}:${Date.now()}`});
      if(reserved.status==='pending'){set(key,file.name,t('files.pending',{name:file.name}),'error');return;}
      if(reserved.status!=='executed'){set(key,file.name,t('files.failed',{name:file.name,reason:reserved.reason}),'error');return;}
      const held=reserved.result as {id:string;status:string;existing:boolean;chunk_size:number;chunks:number};
      if(held.status==='verified'){set(key,file.name,t('files.already',{name:file.name}),'ok');return;}
      const headers=await authHeaders();
      for(let n=0;n<held.chunks;n++){
        set(key,file.name,t('files.uploading',{name:file.name,pct:String(Math.round(n/held.chunks*100))}));
        const piece=file.slice(n*held.chunk_size,Math.min(file.size,(n+1)*held.chunk_size));
        const r=await tries(()=>fetchImpl(`${base()}/${held.id}/chunks/${n}`,{method:'PUT',headers:{...headers,'content-type':'application/octet-stream'},body:piece}));
        if(r.status===409)break; // already verified by an earlier attempt
        if(!r.ok)throw Error(await errorOf(r));
      }
      set(key,file.name,t('files.verifying',{name:file.name}));
      const done=await tries(()=>fetchImpl(`${base()}/${held.id}/complete`,{method:'POST',headers}));
      if(!done.ok)throw Error(await errorOf(done));
      set(key,file.name,t('files.done',{name:file.name}),'ok'); // a resumed reservation is finished now, not 'already kept'
    }catch(e){set(key,file.name,t('files.failed',{name:file.name,reason:(e as Error).message}),'error');}
  }
  async function upload(list:FileList|File[]|null){
    const chosen=Array.from(list??[]);if(!chosen.length||busy)return;
    setBusy(true);setNotice('');setProgress([]);
    try{
      // One at a time: a 100 MB file is hashed in memory, and the chunks of one file finish before the next starts.
      for(const [i,file] of chosen.entries())await send(file,`${i}:${file.name}`);
      setNote('');await files.refetch();
    }finally{setBusy(false);}
  }
  function onDrop(e:DragEvent<HTMLDivElement>){e.preventDefault();setOver(false);void upload(e.dataTransfer.files);}

  async function open(f:RecordFile){
    setNotice('');
    try{
      const r=await fetchImpl(`${base()}${f.kind==='plan'?'/plans':''}/${f.id}/file`,{headers:await authHeaders()});
      if(!r.ok)throw Error(await errorOf(r));
      const url=URL.createObjectURL(await r.blob());
      setOpened({name:f.name,url});
      // The built-in browser pane blocks popups (window.open answers null): try a new tab, and leave the link below.
      const a=document.createElement('a');a.href=url;a.target='_blank';a.rel='noopener';document.body.appendChild(a);a.click();a.remove();
    }catch{setNotice(t('files.openFailed'));}
  }
  async function propose(command:'hide_record_file'|'restore_record_file'|'set_record_file_note',args:Record<string,unknown>){
    setNotice('');
    try{
      const r=await api.propose(client,{command,args,idempotency_key:`${command}:${String(args.id)}:${Date.now()}`});
      if(r.status!=='executed'){setNotice(t('files.saveFailed',{reason:r.status==='pending'?t('opp.needsApproval'):r.reason}));return false;}
      await files.refetch();return true;
    }catch(e){setNotice(t('files.saveFailed',{reason:(e as Error).message}));return false;}
  }

  return <section className={cn('text-sm',props.className??'mt-3 border-t pt-3')} aria-label={t('files.title')}>
    <div className="flex flex-wrap items-baseline justify-between gap-2">
      <h3 className="font-semibold">{t('files.title')}</h3>
      <label className="flex items-center gap-2 text-micro text-muted-foreground"><input type="checkbox" checked={showHidden} onChange={e=>setShowHidden(e.target.checked)}/>{t('files.showHidden')}</label>
    </div>
    <p className="mt-1 text-muted-foreground">{t('files.lede')}{props.jobRef?` ${t('files.carried')}`:''}</p>

    <div className="mt-3 grid gap-3 sm:grid-cols-2">
      <label className="block">{t('files.category')}
        <select className="mt-1 block min-h-11 w-full rounded border bg-background px-2" value={category} onChange={e=>setCategory(e.target.value as RecordFileCategory)} disabled={busy}>
          {RECORD_FILE_CATEGORIES.map(c=><option key={c} value={c}>{catLabel(c)}</option>)}
        </select></label>
      <label className="block">{t('files.note')}
        <input className="mt-1 block min-h-11 w-full rounded border bg-background px-2" value={note} maxLength={1000} placeholder={t('files.notePlaceholder')} onChange={e=>setNote(e.target.value)} disabled={busy}/></label>
    </div>
    <div onDragOver={e=>{e.preventDefault();setOver(true);}} onDragLeave={()=>setOver(false)} onDrop={onDrop}
      className={cn('mt-3 rounded-lg border-2 border-dashed p-4 text-center',over?'border-primary bg-primary/5':'border-border')}>
      <p>{t('files.drop')} <button type="button" className="text-primary underline" disabled={busy} onClick={()=>input.current?.click()}>{t('files.choose')}</button></p>
      <p className="mt-1 text-micro text-muted-foreground">{t('files.types')} · 100 MB</p>
      <input ref={input} type="file" multiple className="hidden" accept={RECORD_FILE_ACCEPT} onChange={e=>{void upload(e.target.files);e.target.value='';}}/>
    </div>
    {progress.length>0&&<ul className="mt-2 space-y-1" aria-live="polite">{progress.map(p=><li key={p.key} role={p.tone==='error'?'alert':'status'} className={cn(p.tone==='error'?'text-destructive':p.tone==='ok'?'text-success':'text-muted-foreground')}>{p.text}</li>)}</ul>}

    {files.isPending&&<p role="status" className="mt-3 text-muted-foreground">{t('files.loading')}</p>}
    {files.isError&&<p role="alert" className="mt-3 text-destructive">{t('files.loadFailed')} <button type="button" className="underline" onClick={()=>void files.refetch()}>{t('files.retry')}</button></p>}
    {files.data?.length===0&&<p className="mt-3 text-muted-foreground">{t('files.empty')}</p>}
    {!!files.data?.length&&<ul className="mt-3 divide-y">{files.data.map(f=><li key={`${f.kind}:${f.id}`} className={cn('py-2',f.hidden&&'opacity-60')}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className="text-left font-medium text-primary underline-offset-2 hover:underline disabled:text-foreground disabled:no-underline" disabled={f.status!=='verified'} onClick={()=>void open(f)} title={t('files.open')}>{f.name}</button>
        <span className="rounded bg-muted px-1.5 text-micro">{catLabel(f.category)}</span>
        {f.readonly&&<span className="rounded bg-accent px-1.5 text-micro">{t('files.planTag')}</span>}
        {props.jobRef&&f.filed_on==='opportunity'&&<span className="text-micro text-muted-foreground">{t('files.fromOpportunity')}</span>}
        {f.hidden&&<span className="text-micro text-muted-foreground">{t('files.hidden')}</span>}
        <span className="ml-auto flex gap-2">
          {!f.readonly&&!f.hidden&&<Button size="sm" variant="ghost" onClick={()=>setEditing({id:f.id,note:f.note??''})}>{t('files.editNote')}</Button>}
          {!f.readonly&&(f.hidden
            ?<Button size="sm" variant="outline" onClick={()=>void propose('restore_record_file',{id:f.id})}>{t('files.restore')}</Button>
            :<Button size="sm" variant="ghost" onClick={()=>void propose('hide_record_file',{id:f.id})}>{t('files.hide')}</Button>)}
        </span>
      </div>
      <p className="text-micro text-muted-foreground">{mb(f.size)} MB · {who(f.by)} · {sydney(f.created_at)}</p>
      {f.status!=='verified'&&<p className="text-micro text-warning">{t('files.unfinished')}</p>}
      {editing?.id===f.id
        ?<div className="mt-1 flex flex-wrap gap-2"><input className="min-h-11 flex-1 rounded border bg-background px-2" value={editing.note} maxLength={1000} placeholder={t('files.notePlaceholder')} onChange={e=>setEditing({id:f.id,note:e.target.value})}/>
          <Button size="sm" onClick={()=>void propose('set_record_file_note',{id:f.id,note:editing.note.trim()}).then(ok=>{if(ok)setEditing(null);})}>{t('files.saveNote')}</Button>
          <Button size="sm" variant="ghost" onClick={()=>setEditing(null)}>{t('files.cancel')}</Button></div>
        :f.note&&<p className="whitespace-pre-wrap">{f.note}</p>}
    </li>)}</ul>}
    {opened&&<p className="mt-2"><a className="text-primary underline" href={opened.url} target="_blank" rel="noopener noreferrer" download={opened.name}>{t('files.openLink',{name:opened.name})}</a></p>}
    {notice&&<p role="alert" className="mt-2 text-destructive">{notice}</p>}
  </section>;
}
